import { ReactElement, useEffect } from 'react';
import {
  Stack,
  TextField,
  Typography,
  Avatar,
  Box,
  FormControlLabel,
  Switch,
  createFilterOptions,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruckFast as VehicleIcon,
  faVectorSquare as LogisticsZonesIcon,
} from '@fortawesome/free-solid-svg-icons';

import {
  Subscription,
  UserInfo,
  AccessFeature,
  LogisiticZoneInfo,
  CustomAutocomplete,
  VehicleInfo,
} from '~components/index';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { getDefaultAvatar } from '~utils/user';
import { PlanFeaturesTypes, Roles } from '~globals/types/enums';
import { ORDERS_ON_AGENDA_STATE_OPTIONS } from '~constants/ordersStatesOptions';
import { SOURCE_IMPORT_TYPES_OPTIONS } from '~constants/sourceImportType';

import { useOrdersFiltersMenuV2Context } from '../../Context';

import { OrdersFiltersMenuV2Inputs } from '../../styles';

const OrdersFiltersMenuV2VariantInTransit = (): ReactElement => {
  const {
    filters: { inTransit: filtersInTransit },
    handleChangeInputFilter,
    handleChangeInputDateFilter,
    selectsData,
  } = useOrdersFiltersMenuV2Context();

  useEffect(() => {
    if (filtersInTransit.activeFilters.itemState?.length) {
      filtersInTransit.autocompletesRefs.itemState.current?.setValue(
        filtersInTransit.activeFilters.itemState,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInTransit.activeFilters.itemState]);

  useEffect(() => {
    if (filtersInTransit.activeFilters.vehicles?.length) {
      filtersInTransit.autocompletesRefs.vehicles.current?.setValue(
        filtersInTransit.activeFilters.vehicles,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInTransit.activeFilters.vehicles]);

  useEffect(() => {
    if (filtersInTransit.activeFilters.drivers?.length) {
      filtersInTransit.autocompletesRefs.drivers.current?.setValue(
        filtersInTransit.activeFilters.drivers,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInTransit.activeFilters.drivers]);

  useEffect(() => {
    if (filtersInTransit.activeFilters.services?.length) {
      filtersInTransit.autocompletesRefs.services.current?.setValue(
        filtersInTransit.activeFilters.services,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInTransit.activeFilters.services]);

  useEffect(() => {
    if (filtersInTransit.activeFilters.zones?.length) {
      filtersInTransit.autocompletesRefs.logisticsZones.current?.setValue(
        filtersInTransit.activeFilters.zones,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInTransit.activeFilters.zones]);

  useEffect(() => {
    if (filtersInTransit.activeFilters.assignedUser) {
      filtersInTransit.autocompletesRefs.assignedUser.current?.setValue(
        filtersInTransit.activeFilters.assignedUser,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInTransit.activeFilters.assignedUser]);

  useEffect(() => {
    if (filtersInTransit.activeFilters.sourceImports?.length) {
      filtersInTransit.autocompletesRefs.sourceImports.current?.setValue(
        filtersInTransit.activeFilters.sourceImports,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInTransit.activeFilters.sourceImports]);

  return (
    <OrdersFiltersMenuV2Inputs>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          &#8288;Precio
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Mayor a"
            name="priceGt"
            value={filtersInTransit.filters.priceGt ?? ''}
            onChange={handleChangeInputFilter(filtersInTransit)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <TextField
            label="Menor a"
            name="priceLt"
            value={filtersInTransit.filters.priceLt ?? ''}
            onChange={handleChangeInputFilter(filtersInTransit)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Stack spacing={0.5} direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            &#8288;Fecha de vencimiento
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={
                  filtersInTransit.filters.includeWithoutExpirationDate ?? false
                }
                onChange={(event) =>
                  filtersInTransit.onChangeFilterInput(
                    'includeWithoutExpirationDate',
                    event.target.checked,
                  )
                }
                size="small"
              />
            }
            label="Sin fecha de vencimiento"
            componentsProps={{
              typography: {
                variant: 'caption',
                fontSize: 10,
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersInTransit.filters.expirationDateTimeFrom}
            onChange={handleChangeInputDateFilter(filtersInTransit)(
              'expirationDateTimeFrom',
            )}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersInTransit.filters.expirationDateTimeTo}
            onChange={handleChangeInputDateFilter(filtersInTransit)(
              'expirationDateTimeTo',
            )}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          &#8288;Fecha de creación
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersInTransit.filters.dateFrom ?? null}
            onChange={handleChangeInputDateFilter(filtersInTransit)('dateFrom')}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersInTransit.filters.dateTo ?? null}
            onChange={handleChangeInputDateFilter(filtersInTransit)('dateTo')}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <CustomAutocomplete
        innerRef={filtersInTransit.autocompletesRefs.itemState}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={val.label.toString()}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={ORDERS_ON_AGENDA_STATE_OPTIONS}
        label="Estados"
        name="itemState"
        onChange={(_e, vals) => {
          filtersInTransit.onChangeFilterInput('itemState', vals);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      />

      <CustomAutocomplete
        innerRef={filtersInTransit.autocompletesRefs.vehicles}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={`${val?.displayName}${val?.code ? ` ${val.code}` : ''}`}
              icon={
                <FontAwesomeIcon
                  icon={VehicleIcon}
                  style={{ color: val.color }}
                />
              }
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.vehicles.list}
        loading={selectsData.vehicles.loading}
        label="Vehículos"
        name="vehicles"
        onChange={(_e, vals) => {
          filtersInTransit.onChangeFilterInput('vehicles', vals);
        }}
        getOptionLabel={(option) =>
          `${option?.displayName}${option?.code ? ` ${option.code}` : ''}`
        }
        renderOption={(props, option) => (
          <li {...props}>
            <VehicleInfo
              vehicle={option.displayName}
              vehicleCode={option.code as string}
              color={option.color}
            />
          </li>
        )}
      />

      <CustomAutocomplete
        innerRef={filtersInTransit.autocompletesRefs.drivers}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              avatar={
                <Avatar
                  src={getDefaultAvatar(val.avatar ?? undefined)}
                  alt={val.displayName}
                />
              }
              label={val.displayName}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.drivers.list}
        loading={selectsData.drivers.loading}
        label="Choferes"
        name="drivers"
        onChange={(_e, vals) => {
          filtersInTransit.onChangeFilterInput('drivers', vals);
        }}
        getOptionLabel={(option) => option.displayName}
        renderOption={(props, option) => (
          <li {...props}>
            <UserInfo
              name={option.displayName}
              urlAvatar={getDefaultAvatar(option.avatar ?? undefined)}
            />
          </li>
        )}
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) => option.displayName.replace(',', ''),
        })}
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitNotPro}
        innerRef={filtersInTransit.autocompletesRefs.services}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={val.label.toString()}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.services.list}
        loading={selectsData.services.loading}
        label="Tipos de servicios"
        name="services"
        onChange={(_e, vals) => {
          filtersInTransit.onChangeFilterInput('services', vals);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.MgmZones}
        innerRef={filtersInTransit.autocompletesRefs.logisticsZones}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              icon={
                val.color ? (
                  <FontAwesomeIcon
                    icon={LogisticsZonesIcon}
                    style={{ color: val.color }}
                  />
                ) : undefined
              }
              label={val.code}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.logisticsZones.list}
        loading={selectsData.logisticsZones.loading}
        label="Zonas logísticas"
        name="zones"
        onChange={(_e, vals) => {
          filtersInTransit.onChangeFilterInput('zones', vals);
        }}
        getOptionLabel={(option) =>
          option.description
            ? `${option.code} ${option.description}`
            : option.code
        }
        renderOption={(props, option) => (
          <li {...props}>
            <LogisiticZoneInfo
              name={option.code}
              description={option.description}
              color={option.color}
              withTooltip={Boolean(option.color)}
            />
          </li>
        )}
        fullWidth
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitNotPro}
        innerRef={filtersInTransit.autocompletesRefs.assignedUser}
        options={selectsData.assignedUser.list}
        loading={selectsData.assignedUser.loading}
        label="Usuario asignado"
        name="assignedUser"
        onChange={(_e, val) => {
          filtersInTransit.onChangeFilterInput('assignedUser', val);
        }}
        fullWidth
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.userId}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <UserInfo
              name={`${option.lastName}, ${option.firstName}`}
              urlAvatar={option.avatarUrl || undefined}
            />
          </li>
        )}
        startAdornment={(val) =>
          val && (
            <Avatar
              src={getDefaultAvatar(val.avatarUrl || undefined)}
              alt={`${val.lastName}, ${val.firstName}`}
              sx={{ width: 20, height: 20 }}
            />
          )
        }
        groupBy={(option) => {
          if (option.roleId === Roles.Drive) {
            return 'Choferes';
          }

          return 'Usuarios';
        }}
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) =>
            `${option.lastName}, ${option.firstName}`.replace(',', ''),
        })}
      />

      <CustomAutocomplete
        innerRef={filtersInTransit.autocompletesRefs.sourceImports}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              avatar={
                <Box
                  component="img"
                  src={val.image.url}
                  alt={val.image.alt}
                  sx={{ height: 18, width: 18, objectFit: 'contain' }}
                />
              }
              label={val.label}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={SOURCE_IMPORT_TYPES_OPTIONS}
        label="Medios de importación"
        name="sourceImports"
        onChange={(_e, vals) => {
          filtersInTransit.onChangeFilterInput('sourceImports', vals);
        }}
        getOptionLabel={(option) => option.label.toString()}
        renderOption={(props, option) => (
          <li {...props}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                component="img"
                src={option.image.url}
                alt={option.image.alt}
                sx={{ height: 25, width: 25, objectFit: 'contain' }}
              />

              <Typography sx={{ fontSize: 14 }}>{option.label}</Typography>
            </Stack>
          </li>
        )}
      />
    </OrdersFiltersMenuV2Inputs>
  );
};

export default OrdersFiltersMenuV2VariantInTransit;
